import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { Calendar as CalendarIcon, TrendingUp, TrendingDown, ChevronRight, Inbox, Download, Pencil } from 'lucide-react';
import { downloadCSV, formatEntryExport } from '../utils/export';

const fmt = (v) => `₹${Math.round(Number(v || 0)).toLocaleString('en-IN')}`;

export default function ManagerHistory() {
  const { getHeaders, API } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEntries();
  }, []); // eslint-disable-line

  const loadEntries = async () => {
    try {
      const res = await axios.get(`${API}/daily-entries`, { headers: getHeaders() });
      setEntries(res.data);
    } catch (err) {
      console.error('Failed to load entries:', err);
    } finally {
      setLoading(false);
    }
  };

  const today = format(new Date(), 'yyyy-MM-dd');

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin w-8 h-8 border-2 border-[color:var(--color-primary)] border-t-transparent rounded-full" />
      </div>
    );
  }

  const handleExport = () => {
    if (!entries.length) return;
    const rows = formatEntryExport(entries);
    downloadCSV(rows, `entry-history-${format(new Date(), 'yyyy-MM-dd')}`);
    toast.success('History exported');
  };

  return (
    <div className="w-full" data-testid="manager-history-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-lg sm:text-xl font-semibold tracking-tight text-[#1A2421]">Entry History</h2>
          <p className="text-sm text-[#5C6B64] mt-1">{entries.length} entries submitted</p>
        </div>
        {entries.length > 0 && (
          <Button variant="outline" size="sm" onClick={handleExport}
            className="border-[#E2E8DF] text-[#5C6B64] hover:text-[#1A2421] hover:bg-[#F3F5F2]"
            data-testid="export-history-csv">
            <Download className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Export CSV
          </Button>
        )}
      </div>

      {entries.length === 0 ? (
        <Card className="border-[#E2E8DF] shadow-sm">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Inbox className="w-12 h-12 text-[#E2E8DF] mb-4" strokeWidth={1.5} />
            <p className="text-[#5C6B64] text-sm">No entries yet. Start by submitting your daily data.</p>
            <Button
              onClick={() => navigate('/manager/entry')}
              className="mt-4 bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white"
              data-testid="go-to-entry-btn"
            >
              Create Entry
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {entries.map(entry => {
            const calc = entry.calculated || {};
            const isEntryToday = entry.date === today;
            // Canonical variance = (net_sales + room_revenue) - (budget + room_budget).
            // Recomputed here so older entries (written before the
            // budget-engine unification) don't show stale F&B-only variance.
            const _net = Number(calc.net_sales || 0);
            const _room = Number(entry.room_revenue || calc.room_revenue || 0);
            const _fbBgt = Number(entry.budget || 0);
            const _roomBgt = Number(entry.room_revenue_budget || calc.room_revenue_budget || 0);
            const _totalRev = _net + _room;
            const _totalBgt = _fbBgt + _roomBgt;
            const _var = _totalRev - _totalBgt;
            const _varPct = _totalBgt > 0 ? Number(((_var / _totalBgt) * 100).toFixed(1)) : 0;
            const variancePositive = _var >= 0;
            const ss = entry.section_submissions || {};
            const hasLockedSections = Object.values(ss).some(s => s?.locked);
            const allMaxEdits = Object.values(ss).every(s => (s?.edit_count || 0) >= 2);
            const canEditEntry = hasLockedSections && !allMaxEdits;
            return (
              <Card
                key={entry.id}
                className="border-[#E2E8DF] shadow-sm metric-card"
                data-testid={`history-entry-${entry.date}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 cursor-pointer flex-1"
                      onClick={() => navigate(`/manager/entry?date=${entry.date}`)}>
                      <div className="w-10 h-10 bg-[#F3F5F2] rounded-lg flex items-center justify-center">
                        <CalendarIcon className="w-5 h-5 text-[color:var(--color-primary)]" strokeWidth={1.5} />
                      </div>
                      <div>
                        <p className="font-medium text-[#1A2421]">
                          {format(parseISO(entry.date), 'dd MMM yyyy')}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5">
                          {isEntryToday && (
                            <Badge className="bg-[#2D6A4F]/10 text-[#2D6A4F] border-[#2D6A4F]/20 text-xs">Today</Badge>
                          )}
                          <span className="text-xs text-[#5C6B64]">
                            Net Sales: {fmt(calc.net_sales)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-right hidden sm:block">
                        <div className="flex items-center gap-1 justify-end">
                          {variancePositive ? (
                            <TrendingUp className="w-3 h-3 text-[#2D6A4F]" />
                          ) : (
                            <TrendingDown className="w-3 h-3 text-[#C45A41]" />
                          )}
                          <span className={`text-xs font-medium ${variancePositive ? 'text-[#2D6A4F]' : 'text-[#C45A41]'}`}>
                            {_varPct > 0 ? '+' : ''}{_varPct}%
                          </span>
                        </div>
                        <p className="text-xs text-[#5C6B64]">
                          FC: {calc.food_cost_pct}% | BC: {calc.beverage_cost_pct}%
                        </p>
                      </div>
                      {canEditEntry && (
                        <Button variant="outline" size="sm"
                          onClick={(e) => { e.stopPropagation(); navigate(`/manager/entry?date=${entry.date}&edit=true`); }}
                          className="h-8 text-xs border-[#E2E8DF] text-[#5C6B64] hover:text-[color:var(--color-primary)] hover:border-[color:var(--color-primary)] ml-2"
                          data-testid={`edit-entry-${entry.date}`}>
                          <Pencil className="w-3 h-3 mr-1" /> Edit
                        </Button>
                      )}
                      <ChevronRight className="w-4 h-4 text-[#E2E8DF] cursor-pointer"
                        onClick={() => navigate(`/manager/entry?date=${entry.date}`)} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
